import { ProduceBackupManager } from '@src/slide/application/service/produce-backup-manager/core/base';
import { IPCProduceBackupManager, IPCProduceBackupManagerDelegates } from '@src/slide/application/service/produce-backup-manager/core/produce-backup-manager.interface';
import DUISnackbar from '@tencent/dui/lib/components/Snackbar';
import { removeURLParameter } from '@src/slide/infrastructure/module/independent/url/param-operation';
import i18n from '@i18next/i18n';
import { getMultUrl, setNewWindowUrl, tencentDocOpenUrl } from '@tencent/tencent_doc_open_url';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import config, { EnterPriseType } from '@src/js/service/enterprise/temp-config';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { V3_DEVR } from '@src/slide/domain/slide-app/core/services/client-vars-service';
import { SlideFile, MutationSource, ISlideFileData } from '@tencent/slide-sdk-model';
import { CommandData } from '@tencent/slide-sdk-model/src/app/command-data';
import _get from 'lodash/get';
import _flatten from 'lodash/flatten';
import { getSnapshotKeyframeFromDB } from '@src/framework/frame/components/features/export/export-snapshot';
import { openUrlForAll } from '@src/slide/infrastructure/module/independent/url/open-url';

declare global {
    interface Window {
        speedReport?: any;
    }
}

export class PCProduceBackupManager extends ProduceBackupManager implements IPCProduceBackupManager {
    declare protected services: IPCProduceBackupManagerDelegates;

    public async createCopyDoc(isOffline: boolean): Promise<boolean> {
        const keyframe = isOffline
            ? await this.getKeyframeFromOfflineStatus()
            : await SlideDataServices.SDKDataServices.PresentationStoreService.promise()
                .then(p => p.getSerializedPresentationDataWithPage());
        const res = await this.createDocBySlideFileData?.(keyframe);
        if (res) {
            this.services.exportManager.promise().then((exportManager) => {
                exportManager.showModalToClearUnSendData();
            });
        }
        return res;
    }

    public redirectToDoc = (url) => {
        if (!url) {
            return;
        }
        if (this.hasExternal()) {
            (external as any).openUrl(null, url, null, '');
        } else {
            if (browser.isPCWechat) {
                setNewWindowUrl(window, url);
            } else if (config.supportEnterprise(EnterPriseType.ALL) || this.injectable.enabledMultipleDomainsFeature) {
                // 企微环境绕过scenario中间页直接打开
                // 支持多域名功能
                openUrlForAll({ url });
            } else {
                // @ts-ignore @hououinliu
                tencentDocOpenUrl(url);
            }
        }
    };

    protected handleCheckBack = () => {
        setTimeout(() => {
            document.getElementById('input-title')?.focus();
            (document.getElementById('input-title') as HTMLTextAreaElement)?.select();
        }, 100);
        DUISnackbar.show({
            message: i18n.t('已生成并打开副本'),
            type: 'success',
            autoClose: true,
        });
        // 去掉url上的 newPad 和 newPadType 参数
        try {
            const tempUrl = removeURLParameter(window.location.href, 'newPad');
            const url = getMultUrl(removeURLParameter(tempUrl, 'newPadType'));
            history.replaceState(null, '', url);
        } catch (e) {
            window.log?.err(e);
        }
    };

    protected handleCreateDocNew = (res) => {
        this.singleSnackbar.close();
        const url = `${res.doc_url}?newPad=1&newPadType=clone`;
        this.redirectToDoc(url);

        // 点击生成副本成功的数据上报
        if (window.tdwReport) {
            window.tdwReport({
                opername: 'server_doc',
                module: 'copy',
                action: 'all',
            });
        }
        this.shouldReport && window.speedReport?.markEnd(window.speedReport.reportId.docDuplicate);
    };

    protected checkShowProducingSnackbar() {
        return !this.hasExternal();
    }

    /**
     * 获取离线状态下文档的 keyframe
     * 1. 有限拉取对应版本号快照，并应用离线数据
     * 2. 降级策略：如果基于版本应用离线数据失败，从 snapshot 中取数据
     * 3. 降级策略：如果从 snapshot 中取数据失败，再次降级，直接使用内存中的数据
     * @returns
     */
    private getKeyframeFromOfflineStatus = async () => {
        const { globalPadId = '', baseRevision = 0 } = await SlideDataServices.DocInfoManager.promise();
        const collaboration = await OnlineEditServices.CollaborationManager.promise();
        const presentationStoreService = await SlideDataServices.SDKDataServices.PresentationStoreService.promise();
        const snapshotManager = await SlideDataServices.SDKDataServices.SnapshotManager.promise();
        const offlineMutationsPacks = collaboration.getPersistent().getMutationsPacks();
        if (!offlineMutationsPacks?.length) {
            // 一般不会出现这种问题，兜个底
            return presentationStoreService.getSerializedPresentationDataWithPage();
        }
        const offlineDataBaseRev = offlineMutationsPacks[0].revision
            ?? baseRevision;
        const docDataByRev = await OnlineEditServices.OnlineApiService()
            ?.getHistorySlide(globalPadId, offlineDataBaseRev, -1);
        const docTextByRev = _get(docDataByRev, 'data.data.initialAttributedText.text');
        const offlineStore = presentationStoreService
            .createFromCommandData(new SlideFile(JSON.parse(docTextByRev)).getCommandData());
        try {
            offlineStore?.applyCommandData(
                CommandData
                    .fromValidMutationData(_flatten(offlineMutationsPacks.map(pack => pack.mutations)))
                    .getCommandData(),
                MutationSource.Offline,
            );
            return offlineStore.getSerializedPresentationDataWithPage();
        } catch (e) {
            // 如果基于版本应用离线数据依然失败，从 snapshot 中取数据
            const snapshots = await snapshotManager.getSnapshotStorage().list();
            if (!snapshots?.length) return presentationStoreService.getSerializedPresentationDataWithPage();
            const [item] = snapshots.reverse();
            const snapshotStorage = snapshotManager.getSnapshotStorage();
            const snapshotItem = await snapshotStorage.getById(item.id);
            try {
                const keyframe = await getSnapshotKeyframeFromDB(snapshotItem as any, true);
                return keyframe;
            } catch (e) {
                // 一般不会出现这种问题，再次兜底
                return presentationStoreService.getSerializedPresentationDataWithPage();
            }
        }
    };

    /**
     * 根据提供的文本内容生成副本，返回创建状态
     * @param {*} keyframe
     * @returns
     */
    private createDocBySlideFileData = async (slideFileData: ISlideFileData) => {
        const title = this.getTitle();
        // 接口回来后再决定是否打开新窗口，此处直接本页显示loading
        if (!this.hasExternal()) {
            this.singleSnackbar.show({ message: i18n.t('生成副本中，请稍候...') });
        }
        const apiService = await OnlineEditServices.OnlineApiService.promise();
        const docInfoManager = await SlideDataServices.DocInfoManager.promise();
        try {
            const res = await apiService.createDocByData({
                title: i18n.t('副本-{0}', { 0: title }),
                atext: JSON.stringify({ text: JSON.stringify({ ...slideFileData, dver: V3_DEVR }) }),
                globalPadId: docInfoManager.globalPadId ?? '',
            });
            const isCreated = await this.onCreateDocNew(res);
            return isCreated;
        } catch (e) {
            this.onCatchCreateDocNew();
        }
        return false;
    };

    private hasExternal = () => typeof (external) !== 'undefined' && typeof ((external as any).openUrl) === 'function';
}
